import { getApiBaseUrl } from '@/config/apiConfig'

/**
 * Measures the round-trip to the API host on a request that does NO server work,
 * so a slow API response can be attributed to the link or to the server.
 *
 * Why this exists (production incident 2026-10-16, Android native): a report PDF
 * rendered synchronously for 44 s and held the user's PHP session lock. Every
 * other request from that user queued behind the lock and hit the 15 s axios
 * timeout. Each of those timeouts was a latency sample of 15 000 ms, so the
 * slow-connection banner appeared on a perfectly healthy network. A request's
 * duration is link time PLUS server time, and only the probe separates the two.
 *
 * Target: `/version.json` on the API origin. It is a static file served by the
 * PWA container behind Caddy — it never reaches PHP, so it cannot wait on a
 * session lock or on a busy worker. The API origin matters on native: there
 * `window.location` is `capacitor://localhost` / `https://localhost`, which is
 * served from the app bundle and would measure nothing. `connectivityProbe.ts`
 * asks the same file on native for a different question (reachability), not a
 * round-trip time, so the two stay separate.
 *
 * `mode: 'no-cors'`: on native the API origin is cross-origin and a static file
 * carries no CORS headers. An opaque response still completes the round-trip,
 * which is all that is timed here.
 */
const PROBE_TIMEOUT_MS = 5_000
const PROBE_PATH = '/version.json'

let inFlight: Promise<number | null> | null = null

function probeUrl(): string {
  let base = ''
  try {
    base = getApiBaseUrl()
  } catch {
    // Dev-only throw on native without VITE_API_URL; fall back to the page origin.
  }
  return new URL(PROBE_PATH, base || window.location.origin).toString()
}

async function runProbe(): Promise<number | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
  const startedAt = Date.now()

  try {
    await fetch(probeUrl(), {
      method: 'HEAD',
      cache: 'no-store',
      credentials: 'omit',
      mode: 'no-cors',
      signal: controller.signal,
    })
    return Date.now() - startedAt
  } catch {
    // A probe that hung until our own abort IS a slow link — report how long it
    // was given. A probe that failed outright (no route, DNS, offline) carries
    // no latency at all, matching how api-client already drops ERR_NETWORK.
    return controller.signal.aborted ? Date.now() - startedAt : null
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Link round-trip in ms, or `null` when the link could not be measured.
 *
 * Concurrent callers share one probe: when a server stall times out several
 * requests at once, they all ask the same question at the same moment, and N
 * probes would only add load to the link being measured.
 */
export function measureLinkLatency(): Promise<number | null> {
  inFlight ??= runProbe().finally(() => {
    inFlight = null
  })
  return inFlight
}
